import {useMemo} from 'react';

import {assertNever} from 'util/Types';

import {RealtimeVoiceSessionControls, RealtimeVoiceSessionState} from './useRealtimeVoiceSession';

/** Product-level states consumed by the composer. Keep provider details out of the view. */
export enum VoiceUIState {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  LISTENING = 'listening',
  AGENT_SPEAKING = 'agentSpeaking',
}

export interface VoiceUIController {
  state: VoiceUIState;
  isListening: boolean;
  isAgentSpeaking: boolean;
  inputLevel?: number;
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

function getVoiceUIState(controls: RealtimeVoiceSessionControls): VoiceUIState {
  switch (controls.sessionState) {
    case RealtimeVoiceSessionState.NOT_CONNECTED:
      return VoiceUIState.IDLE;
    case RealtimeVoiceSessionState.CONNECTING:
      return VoiceUIState.CONNECTING;
    case RealtimeVoiceSessionState.CONNECTED:
      return controls.isAssistantSpeaking ? VoiceUIState.AGENT_SPEAKING : VoiceUIState.LISTENING;
    default:
      return assertNever(controls.sessionState);
  }
}

/** Thin adapter from transport controls to the stable semantic surface used by Voice UI. */
export default function useVoiceUIController(controls: RealtimeVoiceSessionControls): VoiceUIController {
  const {startVoiceSession, stopVoiceSession} = controls;
  const state = getVoiceUIState(controls);
  const isListening = controls.isUserSpeaking || controls.microphoneLevel > 0.02;
  return useMemo(
    () => ({
      state,
      isListening,
      isAgentSpeaking: controls.isAssistantSpeaking,
      inputLevel: controls.microphoneLevel,
      start: startVoiceSession,
      stop: stopVoiceSession,
    }),
    [
      controls.isAssistantSpeaking,
      controls.microphoneLevel,
      isListening,
      startVoiceSession,
      state,
      stopVoiceSession,
    ]
  );
}
